import { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useMilestoneSets, MilestoneTemplateItem } from '@/hooks/useMilestoneSets';
import { MILESTONE_TYPES, ACTION_OWNERS } from '@/lib/types/milestone';

interface MilestoneSetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingSet?: any;
  mode: 'create' | 'edit' | 'view';
  onModeChange?: (mode: 'create' | 'edit' | 'view') => void;
  isAdminView?: boolean;
}

const categories = [
  { value: 'print_materials', label: 'Print Materials' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'signage', label: 'Signage' },
  { value: 'merchandising', label: 'Merchandising' },
  { value: 'other', label: 'Other' },
];

export function MilestoneSetDialog({ open, onOpenChange, editingSet, mode, onModeChange, isAdminView = false }: MilestoneSetDialogProps) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [milestones, setMilestones] = useState<MilestoneTemplateItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isStandard, setIsStandard] = useState(false);

  const { createMilestoneSet, updateMilestoneSet } = useMilestoneSets();

  useEffect(() => {
    if (editingSet) {
      setName(editingSet.name);
      setCategory(editingSet.category || '');
      setDescription(editingSet.description || '');
      setMilestones(editingSet.milestone_template_items || []);
      setIsStandard(editingSet.is_standard || false);
    } else {
      resetForm();
    }
  }, [editingSet]);

  const resetForm = () => {
    setName('');
    setCategory('');
    setDescription('');
    setMilestones([]);
    setIsStandard(false);
  };

  const addMilestone = () => {
    setMilestones([
      ...milestones,
      {
        milestone_name: '',
        sequence_order: milestones.length + 1,
        milestone_type: 'planning',
        deliverable: '',
        action_owner: 'client',
      },
    ]);
  };

  const removeMilestone = (index: number) => {
    setMilestones(milestones.filter((_, i) => i !== index));
  };

  const updateMilestone = (index: number, field: keyof MilestoneTemplateItem, value: any) => {
    const updated = [...milestones];
    updated[index] = { ...updated[index], [field]: value };
    setMilestones(updated);
  };

  const moveMilestone = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === milestones.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const updated = [...milestones];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    
    // Update sequence orders
    updated.forEach((m, i) => {
      m.sequence_order = i + 1;
    });
    
    setMilestones(updated);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      return;
    }

    if (milestones.length === 0) {
      return;
    }

    // Validate all milestones
    const invalid = milestones.some(
      (m) => !m.milestone_name.trim() || !m.deliverable.trim()
    );
    if (invalid) {
      return;
    }

    try {
      setLoading(true);
      
      if (editingSet && mode === 'edit') {
        await updateMilestoneSet(editingSet.id, name, milestones, category, description, isStandard);
      } else {
        await createMilestoneSet(name, milestones, category, description, isStandard);
      }

      onOpenChange(false);
      resetForm();
    } catch (err) {
      console.error('Error saving milestone set:', err);
    } finally {
      setLoading(false);
    }
  };

  const isViewMode = mode === 'view';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isViewMode ? name : mode === 'edit' ? 'Edit Milestone Set' : 'Create New Milestone Set'}
          </DialogTitle>
          <DialogDescription>
            {isViewMode
              ? 'View milestone set details'
              : 'Build a reusable milestone set for your jobs'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Set Details */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Milestone Set Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Standard Manufacturing Project"
                disabled={isViewMode}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={category} onValueChange={setCategory} disabled={isViewMode}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isViewMode && (
                <div>
                  <Label>Milestones</Label>
                  <div className="h-10 flex items-center">
                    <Badge variant="secondary">{milestones.length} milestones</Badge>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description..."
                rows={2}
                disabled={isViewMode}
              />
            </div>

            {isAdminView && !isViewMode && (
              <div className="flex items-center space-x-2 p-4 border border-border rounded-md bg-secondary/20">
                <input
                  type="checkbox"
                  id="is-standard-milestone"
                  checked={isStandard}
                  onChange={(e) => setIsStandard(e.target.checked)}
                  className="h-4 w-4"
                />
                <Label htmlFor="is-standard-milestone" className="cursor-pointer font-medium">
                  Mark as Standard Template
                  <span className="block text-xs text-muted-foreground font-normal mt-1">
                    Standard templates are visible to all organizations
                  </span>
                </Label>
              </div>
            )}
          </div>

          {/* Milestones */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>Milestones *</Label>
              {!isViewMode && (
                <Button type="button" variant="outline" size="sm" onClick={addMilestone}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Milestone
                </Button>
              )}
            </div>

            {milestones.length === 0 ? (
              <Card>
                <CardContent className="flex items-center justify-center py-8 text-muted-foreground">
                  {isViewMode ? 'No milestones' : 'No milestones yet. Add your first milestone to get started.'}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {milestones.map((milestone, index) => (
                  <Card key={index}>
                    <CardContent className="pt-6">
                      <div className="flex gap-3">
                        {!isViewMode && (
                          <div className="flex flex-col gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => moveMilestone(index, 'up')}
                              disabled={index === 0}
                            >
                              <GripVertical className="h-4 w-4" />
                            </Button>
                            <span className="text-xs text-center text-muted-foreground">
                              {index + 1}
                            </span>
                          </div>
                        )}

                        <div className="flex-1 space-y-3">
                          {isViewMode && (
                            <div className="text-sm font-medium text-muted-foreground mb-2">
                              Milestone {index + 1}
                            </div>
                          )}

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label>Name *</Label>
                              <Input
                                value={milestone.milestone_name}
                                onChange={(e) =>
                                  updateMilestone(index, 'milestone_name', e.target.value)
                                }
                                placeholder="Milestone name"
                                disabled={isViewMode}
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label>Type</Label>
                                <Select
                                  value={milestone.milestone_type}
                                  onValueChange={(value) =>
                                    updateMilestone(index, 'milestone_type', value)
                                  }
                                  disabled={isViewMode}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {MILESTONE_TYPES.map((type) => (
                                      <SelectItem key={type.value} value={type.value}>
                                        {type.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>

                              <div>
                                <Label>Owner</Label>
                                <Select
                                  value={milestone.action_owner}
                                  onValueChange={(value) =>
                                    updateMilestone(index, 'action_owner', value)
                                  }
                                  disabled={isViewMode}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {ACTION_OWNERS.map((owner) => (
                                      <SelectItem key={owner.value} value={owner.value}>
                                        {owner.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>

                          <div>
                            <Label>Deliverable *</Label>
                            <Input
                              value={milestone.deliverable}
                              onChange={(e) =>
                                updateMilestone(index, 'deliverable', e.target.value)
                              }
                              placeholder="What specific output is expected?"
                              disabled={isViewMode}
                            />
                          </div>

                          <div>
                            <Label>Description</Label>
                            <Textarea
                              value={milestone.description || ''}
                              onChange={(e) =>
                                updateMilestone(index, 'description', e.target.value)
                              }
                              placeholder="Optional description"
                              rows={2}
                              disabled={isViewMode}
                            />
                          </div>
                        </div>

                        {!isViewMode && (
                          <div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeMilestone(index)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          {isViewMode ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button onClick={() => onModeChange?.('edit')}>Edit</Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={loading || !name.trim() || milestones.length === 0}>
                {loading ? 'Saving...' : mode === 'edit' ? 'Update' : 'Create'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
